import { FC } from 'react';
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
} from '@chakra-ui/react';
import { FormState, UseFormRegister } from 'react-hook-form';

import { CustomerFormFields } from 'page/CustomerPage/types/customerFormField';
import {
  ChannelForm,
  CorporateNumberForm,
  DetailForm,
  EnvironmentForm,
  IdForm,
  NameForm,
  ZeusTokenForm,
  ZeusTokenCreatedAtForm,
  ZeusTokenExpiredForm,
  PoseidonUrlForm,
  PoseidonAgentCpuForm,
  PoseidonAgentMemoryForm,
  EnableVerticalPodAutoScalerForm,
  SystemUpdateGroupForm,
  IapClientIdForm,
  IapClientSecretForm,
  CurrentSystemVersionForm,
  TargetSystemVersionForm,
  StatusForm,
} from 'page/CustomerPage/components/Forms/EditForms/index';
import { Channel } from 'types/customer';
import { ContractList } from './ContractList/index';

type Props = {
  id: number | undefined;
  zeusToken: string;
  zeusTokenCreatedAt: string;
  zeusTokenExpired: string;
  watchChannel: Channel;
  watchStatus: boolean;
  watchEnableVerticalPodAutoScaler: boolean;
  formState: FormState<CustomerFormFields>;
  isOpen: boolean;
  isConfirmButtonDisabled: boolean;
  isDeleteButtonDisabled: boolean;
  register: UseFormRegister<CustomerFormFields>;
  handleClose: () => void;
  handleClickGoConfirmation: () => void;
  handleClickGoDelete: () => void;
};

export const InputModalView: FC<Props> = ({
  id,
  zeusToken,
  zeusTokenCreatedAt,
  zeusTokenExpired,
  watchChannel,
  watchStatus,
  watchEnableVerticalPodAutoScaler,
  formState,
  isOpen,
  isConfirmButtonDisabled,
  isDeleteButtonDisabled,
  register,
  handleClose,
  handleClickGoConfirmation,
  handleClickGoDelete,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={handleClose}
    size="6xl"
    blockScrollOnMount
    closeOnOverlayClick={false}
    closeOnEsc={false}
  >
    <ModalOverlay />
    <ModalContent h="90vh">
      <ModalHeader p="40px 40px 0 40px">顧客情報更新</ModalHeader>
      <ModalCloseButton />
      <ModalBody overflowY="auto" p="0 40px 0 40px">
        <Box
          mt="40px"
          border="1px"
          borderRadius="4px"
          borderColor="blackAlpha.200"
          padding="20px 40px"
        >
          {/* ヘッダ */}
          <Box mb="40px">
            <Heading as="h4" size="md">
              基本情報
            </Heading>
          </Box>
          <Stack spacing="20px">
            {/* ID */}
            <IdForm id={id} />
            {/* 顧客名 */}
            <NameForm register={register} error={formState.errors.name} />
            {/* 顧客詳細 */}
            <DetailForm register={register} error={formState.errors.detail} />
            {/* GCP プロジェクト名 */}
            <EnvironmentForm
              register={register}
              error={formState.errors.environment}
            />
            {/* 顧客番号 */}
            <CorporateNumberForm
              register={register}
              error={formState.errors.corporateNumber}
            />
            {/* 契約種別 */}
            <ChannelForm
              register={register}
              watchChannel={watchChannel}
              error={formState.errors.channel}
            />
            {/* 顧客環境URL */}
            <PoseidonUrlForm
              register={register}
              error={formState.errors.poseidonUrl}
            />
            {/* 顧客ステータス */}
            <StatusForm
              error={formState.errors.status}
              register={register}
              watchStatus={watchStatus}
            />
            {/* システム管理上のグループ */}
            <SystemUpdateGroupForm
              register={register}
              error={formState.errors.systemUpdateGroup}
            />
            {/* システムの現在バージョン */}
            <CurrentSystemVersionForm register={register} />
            {/* システムの次回バージョン */}
            <TargetSystemVersionForm register={register} />
            {/* Zeus トークン */}
            <ZeusTokenForm token={zeusToken} />
            {/* Zeus トークン作成日 */}
            <ZeusTokenCreatedAtForm createdAt={zeusTokenCreatedAt} />
            {/* Zeus トークンステータス */}
            <ZeusTokenExpiredForm expired={zeusTokenExpired} />
            {/* IAP */}
            <SimpleGrid templateColumns="1fr 2fr">
              <FormLabel>IAP</FormLabel>
            </SimpleGrid>
            {/* IAP Client ID */}
            <IapClientIdForm
              register={register}
              error={formState.errors.iapClientId}
            />
            {/* IAP Client Secret */}
            <IapClientSecretForm
              register={register}
              error={formState.errors.iapClientSecret}
            />
            {/* Poseidon設定 */}
            <SimpleGrid templateColumns="1fr 2fr">
              <FormLabel>Poseidon設定</FormLabel>
            </SimpleGrid>
            {/* Poseidon Agent CPU */}
            <PoseidonAgentCpuForm
              register={register}
              error={formState.errors.poseidonAgentCpu}
            />
            {/* Poseidon Agent Memory */}
            <PoseidonAgentMemoryForm
              register={register}
              error={formState.errors.poseidonAgentMemory}
            />
            {/* VPA設定 */}
            <EnableVerticalPodAutoScalerForm
              register={register}
              error={formState.errors.enableVerticalPodAutoScaler}
              watchEnableVerticalPodAutoScaler={
                watchEnableVerticalPodAutoScaler
              }
            />
            {/* ボタン */}
            <Flex flexDirection="column" alignItems="center">
              <Button
                width="140px"
                colorScheme="blackAlpha"
                mr={3}
                onClick={handleClickGoConfirmation}
                isDisabled={isConfirmButtonDisabled}
              >
                確認
              </Button>
            </Flex>
          </Stack>
        </Box>
        <Box
          mt="40px"
          border="1px"
          borderRadius="4px"
          borderColor="blackAlpha.200"
          padding="20px 40px"
        >
          {/* ヘッダ */}
          <Box mb="40px">
            <Heading as="h4" size="md">
              契約済みメニュー
            </Heading>
            <ContractList />
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter justifyContent="center" mt="20px" mb="20px">
        <Button
          width="140px"
          colorScheme="blackAlpha"
          variant="link"
          mr={3}
          onClick={handleClickGoDelete}
          isDisabled={isDeleteButtonDisabled}
        >
          この顧客情報を削除
        </Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);
